import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import { useContactDialogs } from "./ContactDialogs";
import { PHONE, PHONE_DISPLAY, LOGO_URL } from "../data/constants";

const NAV_LINKS = [
  { label: "Főoldal", to: "/" },
  { label: "Bemutatkozás", to: "/bemutatkozas" },
  { label: "ÁSZF", to: "/aszf" },
];

export default function Navbar() {
  const { openBooking, openCallback } = useContactDialogs();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? "bg-white border-b border-border shadow-sm"
          : "bg-white border-b border-border"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex py-3 items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="A/Z logó" className="h-20 sm:h-24 lg:h-28 w-auto object-contain transition-transform group-hover:scale-105" />
            <span className="hidden sm:block leading-tight">
              <span className="font-serif-display text-lg tracking-tight">Pénzügyi Megoldások</span>
              <span className="overline block text-muted-foreground mt-0.5">Hitel · Biztosítás · Kölcsön</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`link-underline text-sm transition-colors ${
                  location.pathname === l.to ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${PHONE}`} data-testid="nav-call-link"
              className="flex items-center gap-2 text-sm mr-2 link-underline">
              <Phone className="h-3.5 w-3.5" />
              {PHONE_DISPLAY}
            </a>
            <Button data-testid="nav-callback-btn" onClick={openCallback}
              variant="outline"
              className="rounded-none border-foreground text-foreground hover:bg-secondary h-11 px-5">
              Visszahívás
            </Button>
            <Button data-testid="nav-booking-btn" onClick={openBooking}
              className="rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-11 px-5 group">
              Időpontfoglalás
              <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Button>
          </div>

          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2" aria-label="Menü">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          data-testid="mobile-menu"
          className="lg:hidden bg-background border-b border-border overflow-hidden"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="font-serif-display text-2xl tracking-tight">
                {l.label}
              </Link>
            ))}
            <a href={`tel:${PHONE}`} data-testid="mobile-call-link"
              className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
            <div className="flex flex-col gap-3 pt-2">
              <Button onClick={openCallback} variant="outline"
                className="rounded-none border-foreground w-full h-12">Telefonos visszahívás</Button>
              <Button onClick={openBooking}
                className="rounded-none bg-foreground text-background w-full h-12">Időpontfoglalás</Button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
